// @ts-strict-ignore
import { TFunction } from 'react-i18next'
import * as yup from 'yup'
import { UKRAINE } from 'constants/contacts'
import { useYupFields } from 'hooks/forms/useYupFields'
import { ContactEntity, ContactEntityType } from 'types/contacts'

export type ContactFormData = {
  entityType: ContactEntityType
  companyName: string
  contactDescription: string
  companyRegistrationNumber: string
  companyRegistrationCountry: string
  addressLine1: string
  addressLine2: string
  city: string
  postCode: string
  region: string
  regionCode: string
  firstName: string
  lastName: string
  countryOfResidence: string
  dateOfBirth: string
  isSelfOwned: boolean
}

export type ContactFormKeys = keyof ContactFormData

interface ContactSchemaProps {
  t: TFunction<'translation', undefined>
  requiredFields?: string[]
  isCryptoFlow: boolean
}

export const contactSchema = ({ t, requiredFields, isCryptoFlow }: ContactSchemaProps) => {
  const { getRequiredYupField, getConditionalYupField, getYupField, getCheckboxYupField } =
    useYupFields()

  return yup.object<Record<ContactFormKeys, yup.AnySchema>>({
    entityType: yup.string().required(),
    companyName: getConditionalYupField({
      label: t('formFields.companyName'),
      fieldKey: 'entityType',
      fieldValue: ContactEntity.COMPANY,
    }),
    contactDescription: getRequiredYupField({
      label: t('formFields.contactDescription'),
    }),
    companyRegistrationNumber: yup
      .string()
      .label(t('formFields.companyRegistrationNumber'))
      .when(['entityType'], {
        is: entityType => {
          return (
            entityType === ContactEntity.COMPANY &&
            (isCryptoFlow || requiredFields?.includes('companyRegistrationNumber'))
          )
        },
        then: getRequiredYupField({
          label: t('formFields.companyRegistrationNumber'),
          requiredErrorMessage: t('formFields.required.companyRegistrationNumber'),
        }),
        otherwise: schema => schema.notRequired(),
      }),
    companyRegistrationCountry: yup
      .string()
      .label(t('formFields.companyRegistrationCountry'))
      .when(['entityType'], {
        is: entityType => {
          return (
            entityType === ContactEntity.COMPANY &&
            (isCryptoFlow || requiredFields?.includes('companyRegistrationCountry'))
          )
        },
        then: getRequiredYupField({
          label: t('formFields.companyRegistrationCountry'),
          requiredErrorMessage: t('formFields.required.companyRegistrationCountry'),
        }),
        otherwise: schema => schema.notRequired(),
      }),
    addressLine1: yup
      .string()
      .label(t('formFields.addressLine1'))
      .when(['entityType', 'companyRegistrationCountry', 'countryOfResidence'], {
        is: () => {
          return isCryptoFlow || requiredFields?.includes('addressLine1')
        },
        then: getRequiredYupField({
          label: t('formFields.addressLine1'),
          requiredErrorMessage: t('formFields.required.addressLine1'),
        }),
        otherwise: schema => schema.notRequired(),
      }),
    addressLine2: getYupField({
      label: t('formFields.addressLine2'),
    }).nullable(),
    city: yup
      .string()
      .label(t('formFields.city'))
      .when(['entityType', 'companyRegistrationCountry', 'countryOfResidence'], {
        is: () => {
          return isCryptoFlow || requiredFields?.includes('city')
        },
        then: getRequiredYupField({
          label: t('formFields.city'),
          requiredErrorMessage: t('formFields.required.city'),
        }),
        otherwise: schema => schema.notRequired(),
      }),
    postCode: yup
      .string()
      .label(t('formFields.postCode'))
      .when(['entityType', 'companyRegistrationCountry', 'countryOfResidence'], {
        is: () => {
          return isCryptoFlow || requiredFields?.includes('postCode')
        },
        then: getRequiredYupField({
          label: t('formFields.postCode'),
          requiredErrorMessage: t('formFields.required.postCode'),
        }),
        otherwise: schema => schema.notRequired(),
      }),
    region: yup
      .string()
      .nullable()
      .label(t('formFields.region'))
      .when(['entityType', 'companyRegistrationCountry', 'countryOfResidence'], {
        is: () => {
          return !isCryptoFlow && requiredFields?.includes('region')
        },
        then: getRequiredYupField({
          label: t('formFields.region'),
          requiredErrorMessage: t('formFields.required.region'),
        }),
        otherwise: schema => schema.notRequired(),
      }),
    regionCode: yup
      .string()
      .nullable()
      .label(t('formFields.region'))
      .when(['entityType', 'companyRegistrationCountry', 'countryOfResidence'], {
        is: (
          entityType: string,
          companyRegistrationCountry: string,
          countryOfResidence: string
        ) => {
          const isRequired =
            (entityType === ContactEntity.COMPANY && companyRegistrationCountry === UKRAINE) ||
            (entityType === ContactEntity.INDIVIDUAL && countryOfResidence === UKRAINE)

          return isRequired
        },
        then: getRequiredYupField({
          label: t('formFields.region'),
          requiredErrorMessage: t('formFields.required.region'),
        }),
        otherwise: schema => schema.notRequired(),
      }),
    firstName: getConditionalYupField({
      label: t('formFields.firstName'),
      fieldKey: 'entityType',
      fieldValue: ContactEntity.INDIVIDUAL,
    }),
    lastName: getConditionalYupField({
      label: t('formFields.lastName'),
      fieldKey: 'entityType',
      fieldValue: ContactEntity.INDIVIDUAL,
    }),
    countryOfResidence: yup
      .string()
      .nullable()
      .label(t('formFields.countryOfResidence'))
      .when(['entityType'], {
        is: (entityType: string) => {
          const isRequired =
            entityType === ContactEntity.INDIVIDUAL &&
            (isCryptoFlow || requiredFields?.includes('countryOfResidence'))

          return isRequired
        },
        then: getRequiredYupField({
          label: t('formFields.countryOfResidence'),
          requiredErrorMessage: t('formFields.required.countryOfResidence'),
        }),
        otherwise: schema => schema.notRequired(),
      }),
    dateOfBirth: yup.string().when(['entityType'], {
      is: (entityType: string) => {
        const isRequired =
          entityType === ContactEntity.INDIVIDUAL &&
          (isCryptoFlow || requiredFields?.includes('dateOfBirth'))

        return isRequired
      },
      then: getRequiredYupField({
        label: t('formFields.region'),
        requiredErrorMessage: t('formFields.required.region'),
      }),
      otherwise: schema => schema.notRequired(),
    }),
    isSelfOwned: getCheckboxYupField(t('formFields.myCompany')),
  })
}
